import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { Box, Checkbox, ListSubheader, Radio, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { convertToEditorState } from '../../screens/general/activities-v2/AllPrograms/AllProgramsHelpers';

const SEARCH_THRESHOLD = 8;
const UNGROUPED_HEADING = 'Other';

type PickerProgram = {
  programId: string;
  name: string;
  description: string | null;
  programGroup?: { name: string; displayOrder: number } | null;
};

interface Props {
  programs: PickerProgram[];
  multiple: boolean;
  disabled?: boolean;
  value: string[];
  onChange: (programIds: string[]) => void;
  programLabel: string;
}

export const SUYPProgramPicker = (props: Props) => {
  const { programs, multiple, disabled, value, onChange, programLabel } = props;
  const searchable = programs.length > SEARCH_THRESHOLD;
  const grouped = programs.some((program) => program.programGroup);

  const sortedPrograms = useMemo(
    () =>
      grouped
        ? [...programs].sort(
            (a, b) =>
              (a.programGroup?.displayOrder ?? Number.MAX_SAFE_INTEGER) -
              (b.programGroup?.displayOrder ?? Number.MAX_SAFE_INTEGER)
          )
        : programs,
    [programs, grouped]
  );

  const selected = useMemo(() => programs.filter((program) => value.includes(program.programId)), [programs, value]);

  const placeholder = multiple
    ? `${searchable ? 'Search' : 'Select'} ${pluralize(programLabel).toLowerCase()}`
    : `${searchable ? 'Search' : 'Select'} a ${programLabel.toLowerCase()}`;

  return (
    <Autocomplete<PickerProgram, boolean, boolean>
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disabled={disabled}
      options={sortedPrograms}
      groupBy={grouped ? (program) => program.programGroup?.name ?? UNGROUPED_HEADING : undefined}
      renderGroup={(params) => [
        <ListSubheader key={params.key} disableSticky style={{ fontSize: 13, fontWeight: 600, lineHeight: '32px' }}>
          {params.group}
        </ListSubheader>,
        params.children,
      ]}
      value={multiple ? selected : selected[0] ?? null}
      onChange={(_unusedEvent, newValue) => {
        if (Array.isArray(newValue)) onChange(newValue.map((program) => program.programId));
        else onChange(newValue ? [newValue.programId] : []);
      }}
      getOptionLabel={(program) => program.name}
      getOptionSelected={(option, selectedOption) => option.programId === selectedOption.programId}
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        if (!query) return options;
        return options.filter(
          (program) =>
            program.name.toLowerCase().includes(query) || (program.description ?? '').toLowerCase().includes(query)
        );
      }}
      noOptionsText={`No ${pluralize(programLabel).toLowerCase()} found`}
      style={{ maxWidth: 540 }}
      renderOption={(program, { selected: isSelected }) => (
        <Box display='flex' alignItems='flex-start' width={1}>
          {multiple ? (
            <Checkbox color='primary' checked={isSelected} style={{ marginRight: 8, padding: 4 }} />
          ) : (
            <Radio color='primary' checked={isSelected} style={{ marginRight: 8, padding: 4 }} />
          )}
          <Box minWidth={0}>
            <Typography variant='body1'>{program.name}</Typography>
            {program.description && (
              <Box maxHeight={60} overflow='hidden'>
                <Typography variant='body2' color='textSecondary' component='div' style={{ fontSize: '0.8rem' }}>
                  <CampfireRichEditor editorState={convertToEditorState(program.description)} readOnly />
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant='outlined'
          placeholder={selected.length === 0 ? placeholder : undefined}
          inputProps={{ ...params.inputProps, readOnly: !searchable }}
        />
      )}
    />
  );
};
